import { SortDirection } from '@/enums/sort';
import { TableColumn } from '@/ui/Table';
import { ClearFilterButton } from '@/ui/ClearFilterButton';
import { BrandGroupEditorBrand } from '@/components/brands-table/use-brands-group-editor';
import { BrandsTableData } from '@/components/brands-table/use-brands-table-view-model';
import {
    createGroupTableHeaderSecondaryRow,
    createGroupTableMetricColumns,
    createGroupTableManageColumn,
    createGroupTableNameColumn,
    renderGroupTableCountDetailsCell,
    renderGroupTableTextFilterInput,
} from '@/components/group-tables/group-table-column-primitives';

type CreateBrandsTableColumnsArgs = {
    pickedBrandGroups: string[];
    isHeaderChecked: boolean;
    onHeaderCheckboxChange: (checked: boolean) => void;
    nameFilter: string;
    setNameFilter: (value: string) => void;
    brandFilter: string;
    setBrandFilter: (value: string) => void;
    onGroupEditorOpen: (brandGroup: BrandsTableData) => void;
};

export const createBrandsTableColumns = ({
    pickedBrandGroups,
    isHeaderChecked,
    onHeaderCheckboxChange,
    nameFilter,
    setNameFilter,
    brandFilter,
    setBrandFilter,
    onGroupEditorOpen,
}: CreateBrandsTableColumnsArgs) => {
    const groupEditorColumns: TableColumn<BrandGroupEditorBrand>[] = [
        {
            key: 'name',
            alias: 'Название',
            className: 'min-w-[260px] max-w-[60vw] lg:max-w-none',
            render: (value) => value || '-',
        },
        {
            key: 'productCount',
            alias: 'Продуктов',
            className: 'min-w-[140px] lg:whitespace-nowrap',
        },
    ];

    const columns: TableColumn<BrandsTableData>[] = [
        createGroupTableNameColumn<BrandsTableData>(),
        {
            key: 'brandCount',
            alias: 'Брендов',
            sort: SortDirection.NONE,
            className: 'min-w-[128px] md:min-w-[160px] max-w-[56vw] md:max-w-[34vw] lg:max-w-[280px]',
            render: (_, record) => {
                const brandGroup = record as BrandsTableData;
                return renderGroupTableCountDetailsCell({
                    count: brandGroup.brandCount,
                    details: brandGroup.brandNames,
                });
            },
        },
        {
            key: 'productCount',
            alias: 'Продуктов',
            sort: SortDirection.NONE,
            className: 'min-w-[60px] md:min-w-[72px] lg:whitespace-nowrap',
        },
        ...createGroupTableMetricColumns<BrandsTableData>(),
        createGroupTableManageColumn<BrandsTableData>({
            onOpen: onGroupEditorOpen,
        }),
    ];

    const headerSecondaryRow = createGroupTableHeaderSecondaryRow({
        columns,
        hasSelectionColumn: pickedBrandGroups !== undefined,
        isHeaderChecked,
        onHeaderCheckboxChange,
        renderFilterCell: (column) => {
            if (column.key === 'name') {
                return (
                    <div className="flex h-[34px] min-w-0 items-center gap-1">
                        <div className="min-w-0 flex-1">
                            {renderGroupTableTextFilterInput({
                                id: 'brands-table-filter-name',
                                ariaLabel: 'Фильтр названия группы брендов',
                                placeholder: 'Группа',
                                value: nameFilter,
                                onChange: setNameFilter,
                            })}
                        </div>
                        {!!nameFilter && (
                            <ClearFilterButton
                                ariaLabel="Очистить фильтр группы брендов"
                                onClick={() => setNameFilter('')}
                            />
                        )}
                    </div>
                );
            }

            if (column.key === 'brandCount') {
                return (
                    <div className="flex h-[34px] min-w-0 items-center gap-1">
                        <div className="min-w-0 flex-1">
                            {renderGroupTableTextFilterInput({
                                id: 'brands-table-filter-brand-name',
                                ariaLabel: 'Фильтр бренда в таблице групп брендов',
                                placeholder: 'Бренд',
                                value: brandFilter,
                                onChange: setBrandFilter,
                            })}
                        </div>
                        {!!brandFilter && (
                            <ClearFilterButton ariaLabel="Очистить фильтр бренда" onClick={() => setBrandFilter('')} />
                        )}
                    </div>
                );
            }

            return null;
        },
    });

    return {
        columns,
        headerSecondaryRow,
        groupEditorColumns,
    };
};
